import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Settings, Phone, User, Calendar, CheckCircle } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { APP_NAME } from '../config/branding';

const SeeItInAction: React.FC = () => {
  usePageTitle('Live Demo - Watch AI Handle Real Veterinary Appointment Calls');
  
  // Track sales link clicks
  useSalesTracking();
  
  return (
    <div className="min-h-screen bg-white">
      {/* Video Schema for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          "name": `${APP_NAME} AI Front Desk Demo - Live Call Handling`,
          "description": `Watch a complete demonstration of ${APP_NAME}'s AI phone agent handling real veterinary appointment calls. See how it schedules appointments, answers questions, and manages your front desk automatically.`,
          "thumbnailUrl": "https://helppet.ai/helppetai.png",
          "uploadDate": "2025-01-01T00:00:00Z",
          "duration": "PT3M45S",
          "embedUrl": "https://www.loom.com/embed/152cffb795f9439eb0034d1a13b74f31",
          "contentUrl": "https://helppet.ai/see-it-in-action"
        })}
      </script>

      {/* Hero Section - Notion Style */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="mb-4">
          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 text-sm font-medium rounded-full">
            Live Demo
          </span>
        </div>
        
        <h1 className="text-5xl font-bold text-gray-900 mb-6">
          See {APP_NAME} in Action
        </h1>
        
        <p className="text-xl text-gray-600 mb-8 max-w-3xl">
          Watch a complete end-to-end demonstration of how {APP_NAME} transforms your veterinary practice with intelligent call handling and seamless appointment scheduling.
        </p>
        
        <div className="flex gap-4 mb-16">
          <Link 
            to="/signup" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Get {APP_NAME} Today
          </Link>
          <Link 
            to="/contact" 
            className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Talk to sales →
          </Link>
        </div>

        {/* Video Section */}
        <div className="mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-200">
            <div style={{ position: 'relative', paddingBottom: '64.86486486486486%', height: 0 }}>
              <iframe 
                src="https://www.loom.com/embed/152cffb795f9439eb0034d1a13b74f31?sid=0b5b2026-0337-4b16-8e6c-c69a8d2303a5&t" 
                frameBorder="0" 
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title={`${APP_NAME} Demo Video`}
              />
            </div>
          </div>
        </div>

        {/* What You'll See */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What you'll see in this demo
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Voice Agent in Action */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Play className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">See the Voice Agent in Action</h3>
                <p className="text-sm text-gray-600">
                  Experience how our AI handles real pet owner calls with natural, professional conversations that feel human.
                </p>
              </div>
            </div>

            {/* Configure Work Schedule */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Configure the Work Schedule</h3>
                <p className="text-sm text-gray-600">
                  Set up your practice hours, availability windows, and appointment slots in minutes, not hours.
                </p>
              </div>
            </div>

            {/* Pet Owner Calls */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">A Pet Owner Will Call</h3>
                <p className="text-sm text-gray-600">
                  Watch how seamlessly the AI greets callers, understands their needs, and guides them through the process.
                </p>
              </div>
            </div>

            {/* Account Creation */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add Their Account & Pet</h3>
                <p className="text-sm text-gray-600">
                  See how the system collects pet owner information and pet details naturally during the conversation.
                </p>
              </div>
            </div>

            {/* Appointment Scheduling */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Schedule an Appointment</h3>
                <p className="text-sm text-gray-600">
                  Watch the AI automatically find available time slots that honor your configured work schedule and book appointments instantly.
                </p>
              </div>
            </div>

            {/* Customization */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Configure Voice, Hours & More</h3>
                <p className="text-sm text-gray-600">
                  Customize everything from the agent's voice and greeting to business hours and special instructions all from one dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to automate your front desk?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join veterinary practices saving 80% of phone time with AI-powered call handling.
          </p>
          <div className="flex gap-4 justify-center">
            <Link 
              to="/signup" 
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Get Started Free
            </Link>
            <Link 
              to="/pricing" 
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-medium border-2 border-gray-300 hover:border-gray-400 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeeItInAction;

